import React, { useMemo, useContext } from "react";
import { Form, Input, InputNumber, Select, message } from "antd";
import { useMutation } from "@apollo/client";
import { isFunction, isArray, isNumber, isEmpty } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { isAllLocationSelected } from "../../../../library/helpers";
import { startDrawerLoading, stopDrawerLoading } from "../../../../library/drawerLoadingService";
import useFetchUsers from "../../../../hooks/useFetchUsers";
import { UserContext } from "../../../../amplify/authenticator-provider";
import { getSelectedLead, incrementMessageNotesCount } from "../../../../redux/slices/leadSlice";
import CustomButton from "../../../commonComponents/button";
import { CREATE_MESSAGE_NOTES } from "../../../../graphql/mutation";
import useLang from "../../../../hooks/useLang";

const { TextArea } = Input;

const SendDirectMessage = ({
  user,
  messageId,
  form: externalForm,
  onCancel,
  refetchNotes,
  successCallback,
  preferences,
}) => {
  const [lang] = useLang();

  const { user: loggedInUser } = useContext(UserContext);
  const dispatch = useDispatch();

  const [localForm] = Form.useForm();
  const form = externalForm || localForm;
  const [createMessageNotes, { loading: creatingNotes }] = useMutation(CREATE_MESSAGE_NOTES, {
    onCompleted: async data => {
      // Stop loading when mutation completes
      stopDrawerLoading();

      if (data?.createMessageNotes?.statusCode === 200) {
        if (messageId) {
          dispatch(incrementMessageNotesCount({ messageId }));
        }

        message.success(data.message || "Message note created successfully");
        if (isFunction(successCallback)) {
          successCallback();
        }

        if (isFunction(refetchNotes)) {
          try {
            await refetchNotes();
          } catch (e) {
            console.error("refetchNotes error:", e);
          }
        }
      } else {
        message.error(data?.createMessageNotes?.message);
      }
    },
    onError: err => {
      // Stop loading when mutation fails
      stopDrawerLoading();
      message.error(err.message || "Failed to create message note");
    },
  });

  const selectedLead = useSelector(getSelectedLead);

  const locationId = isAllLocationSelected()
    ? selectedLead?.location?.id
    : sessionStorage.getItem("currentLocationId") || "";

  // Uses setting flag (isActiveUserRendering) to determine active or all users
  const { users, loading: userLoader } = useFetchUsers(null, null, !loggedInUser?.company?.id, {
    companyId: loggedInUser?.company?.id,
    locationIds: [+locationId],
  });

  // Simplified schema with top-level fields: users and message
  const formSchema = useMemo(
    () => ({
      users: {
        placeholder: "Select user",
        label: "Select the users you want to notify",
        type: "select",
        multiple: true,
        value: [user?.id?.toString()],
        required: true,
        options: (() => {
          const opts = [];

          // Always add the user from props first if available
          if (user && user.id) {
            const userLabel =
              user.fullName || `${user.firstName} ${user.lastName}` || "Selected User";
            opts.push({
              label: userLabel,
              value: user.id?.toString(),
            });
          }

          // Add users from users, avoiding duplicates
          if (isArray(users)) {
            users.forEach(u => {
              const exists = opts.find(opt => String(opt.value) === String(u.id));
              if (!exists) {
                opts.push({
                  label: u.fullName || `${u.firstName} ${u.lastName}`,
                  value: u.id?.toString(),
                });
              }
            });
          }

          return opts;
        })(),
      },
      message: {
        label: "Message",
        placeholder: "Write your message here...",
        type: "textarea",
        value: "",
        required: true,
        rows: 6,
      },
    }),
    [users, user]
  );

  // Flatten schema to renderable array: [{ name: ['metadata','subject'], schema: {...} }, ...]
  const fields = useMemo(() => {
    return Object.keys(formSchema).map(fieldKey => ({
      name: [fieldKey],
      schema: formSchema[fieldKey],
      key: fieldKey,
    }));
  }, [formSchema]);

  const renderField = ({ name, schema }) => {
    const rules = [];
    const labelText = schema.label || name.join(".");
    if (schema.required) {
      // For multi-selects, required should validate an array
      if (schema.multiple) {
        rules.push({
          type: "array",
          required: true,
          message: `${labelText} is required`,
        });
      } else {
        rules.push({
          required: true,
          message: `${labelText} is required`,
        });
      }
    }
    if (schema.type === "number") {
      if (isNumber(schema.min) || isNumber(schema.max)) {
        const rule = { type: "number" };
        if (isNumber(schema.min)) rule.min = schema.min;
        if (isNumber(schema.max)) rule.max = schema.max;
        // Friendly message for numeric range
        if (isNumber(schema.min) && isNumber(schema.max)) {
          rule.message = `${labelText} must be between ${schema.min} and ${schema.max}`;
        } else if (isNumber(schema.min)) {
          rule.message = `${labelText} must be at least ${schema.min}`;
        } else if (isNumber(schema.max)) {
          rule.message = `${labelText} must be at most ${schema.max}`;
        }
        rules.push(rule);
      }
    }

    const fieldName = name;

    let inputNode = null;
    switch (schema.type) {
      case "textarea":
        inputNode = <TextArea rows={schema.rows || 4} placeholder={schema.placeholder} />;
        break;
      case "select":
        inputNode = (
          <Select
            placeholder={schema.placeholder}
            loading={userLoader}
            maxTagCount="responsive"
            mode={schema.multiple ? "multiple" : undefined}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const label = (option?.label ?? option?.children ?? "").toString();
              return label.toLowerCase().includes((input || "").toLowerCase());
            }}
          >
            {(schema.options || []).map(opt => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        );
        break;
      case "number":
        inputNode = (
          <InputNumber
            style={{ width: "100%" }}
            placeholder={schema.placeholder}
            min={schema.min}
            max={schema.max}
          />
        );
        break;
      default:
        inputNode = <Input placeholder={schema.placeholder} />;
    }

    return (
      <Form.Item
        key={name.join(".")}
        name={fieldName}
        rules={rules}
        initialValue={schema.value}
        label={
          schema.label ? (
            <span className="text-black/[0.88] text-sm font-normal">{schema.label}</span>
          ) : undefined
        }
      >
        {inputNode}
      </Form.Item>
    );
  };

  const onFinish = async () => {
    try {
      // Check if all forward options are disabled
      const hasAnyForwardOption =
        preferences && Object.values(preferences).some(value => value === true);

      if (!hasAnyForwardOption) {
        message.info(lang.notesPreferenceInfo);
        return;
      }

      // Start loading when form submission begins
      startDrawerLoading();

      // Read the latest form values directly from the Form instance
      const currentValues = form.getFieldsValue(true);

      // Helper function: return selected user IDs directly using schema options
      const getSelectedUserIds = selectedUserIds => {
        return (selectedUserIds || [])
          .map(userId => {
            const optionUser = formSchema.users.options.find(
              opt => String(opt.value) === String(userId)
            );
            return optionUser ? optionUser.value : null;
          })
          .filter(Boolean); // Remove null values
      };

      const selectedUserIds = getSelectedUserIds(currentValues.users || []);

      // Create message notes via GraphQL mutation
      const userIds = selectedUserIds;

      if (isEmpty(userIds)) {
        message.error("Please select at least one user to notify.");
      }

      const variables = {
        messageId: +messageId,
        notes: currentValues.message,
        userIds,
        messageForwardOptions: preferences,
      };

      await createMessageNotes({ variables });
    } catch (err) {
      // Stop loading if there's an error before mutation
      stopDrawerLoading();
      message.error("Failed to create message note");
    }
  };

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{
          users: user?.id ? [user.id?.toString()] : [],
          message: "",
        }}
      >
        {/* Render the two top-level fields */}
        <div className="mb-3">
          <div className="grid grid-cols-1 gap-2">{fields.map(f => renderField(f))}</div>
        </div>

        {!externalForm && (
          <Form.Item>
            <div className="flex w-full justify-start gap-3">
              <CustomButton
                btnText="Save"
                type="primary"
                htmlType="submit"
                loading={creatingNotes}
              />
              <CustomButton btnText="Cancel" handleClick={onCancel} />
            </div>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default SendDirectMessage;
